import React, { Component } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { Button, Typography } from '@mui/material';
import { Navigate } from 'react-router-dom';

import UserDetails from '../components/user/UserDetails';
import ContentBox from '../components/layout/ContentBox';
import ChangePasswordDialog from '../components/user/ChangePasswordDialog';
import InstituteView from './InstituteView';

import {
  showNotification,
  setLoading,
} from '../redux/actions';
import ApiClient from '../loopback/apiClient';
import { QuipUser, RootState } from '../redux/types';
import BillingDashboard from '../components/billing/BillingDashboard';

type State = {
  changePasswordDialogOpen: boolean;
  quipuser?: QuipUser;
}

class UserView extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      changePasswordDialogOpen: false,
    };
  }

  componentDidMount() {
    const { userId } = this.props;
    if (userId) {
      this.fetchUser();
    }
  }

  componentDidUpdate(prevProp: Props) {
    const { userId: prevUserId } = prevProp;
    const { userId } = this.props;
    if (prevUserId !== userId && userId) {
      this.fetchUser();
    }
  }

  fetchUser = async () => {
    const { userId, setLoading: dispatchSetLoading } = this.props;
    if (!userId) {
      return;
    }
    dispatchSetLoading(true);
    const quipuser = await ApiClient.getUser(userId).finally(() => dispatchSetLoading(false));
    this.setState({ quipuser });
  };

  changePassword = (oldPassword: string, newPassword: string) => {
    const {
      showNotification: showNotificationAction,
    } = this.props;
    return ApiClient.changePassword(oldPassword, newPassword)
      .then(() => showNotificationAction(<FormattedMessage id="changePassword.success" />));
  };

  renderWelcome = () => {
    const { email } = this.props;
    const { changePasswordDialogOpen } = this.state;
    return (
      <ContentBox title={<FormattedMessage id="account.welcome" />}>
        <Typography variant="h5"><FormattedMessage id="account.customerInformation" /></Typography>
        <Typography variant="body1" component="span">
          <FormattedMessage id="account.loginMessage" />
          :
          {email}
        </Typography>
        <Button
          variant="contained"
          type="submit"
          sx={{
            color: 'white',
            backgroundColor: 'primary.main',
            margin: '0 0.5em 0 0.5em',
          }}
          id="UserView-changePasswordButton"
          onClick={() => {
            this.setState({
              changePasswordDialogOpen: true,
            });
          }}
        >
          <FormattedMessage id="account.changePasswordLabel" />
        </Button>
        <ChangePasswordDialog
          isOpen={changePasswordDialogOpen}
          onClose={() => this.setState({ changePasswordDialogOpen: false })}
          onPasswordSubmit={this.changePassword}
        />
      </ContentBox>
    );
  };

  renderUserDetails = () => {
    const {
      showNotification: showNotificationAction,
    } = this.props;
    const { quipuser } = this.state;
    if (!quipuser) {
      return null;
    }
    return (
      <UserDetails
        user={quipuser}
        onChange={this.fetchUser}
        showNotification={showNotificationAction}
      />
    );
  };

  render() {
    const {
      userId,
      email,
    } = this.props;
    const {
      quipuser,
    } = this.state;
    if (!userId || !email) {
      return <Navigate to="/login?reason=unauthorize" />;
    }

    return (
      <div>
        <>
          {this.renderWelcome()}
          {
            quipuser
            && this.renderUserDetails()
          }
        </>
        <InstituteView />
        <ContentBox title={<FormattedMessage id="billingAddress.title" />}>
          <BillingDashboard />
        </ContentBox>
      </div>
    );
  }
}

const mapStateToProps = (state: RootState) => ({
  userId: state.users?.token?.userId,
  email: state.users?.token?.email,
});

const dispatchProps = {
  showNotification,
  setLoading,
};

const connector = connect(mapStateToProps, dispatchProps);
type PropsFromRedux = ConnectedProps<typeof connector>;
type Props = PropsFromRedux;

export default connector(UserView);
